import { addressCount, contains, networkAddress } from './ipv4'
import type { Cidr } from './types'
import type { SubnetTree } from './tree'

/**
 * Geometry for the nested split/join bracket drawn beside the subnet table.
 *
 * The bracket is a set of vertical bars, one column per level of the hierarchy,
 * each bar covering the rows of the block it represents. It is built with table
 * `rowspan`/`colspan` rather than absolute positioning, so it stays aligned with
 * the rows no matter how tall they render.
 *
 * The rule that makes it work: a block's bar is emitted **only on its first leaf**
 * — the one whose network address equals the block's — and `rowSpan` stretches it
 * down over the rest. Every other row inside that block emits nothing for it, and
 * its split cell widens to fill the columns no ancestor occupies.
 */
export interface JoinBar {
  /** The block this bar collapses when activated. */
  readonly cidr: Cidr
  /** Levels below the root: the root's own bar is 0. */
  readonly depth: number
  /** Leaf rows this bar covers. Always at least 2 — a bar never spans one row. */
  readonly rowSpan: number
}

export interface BracketRow {
  readonly leaf: Cidr
  /** Levels below the root; 0 for an undivided design. */
  readonly depth: number
  /** Columns the leaf's own split cell fills, left of any ancestor bars. */
  readonly splitColSpan: number
  /** Bars starting on this row, deepest first — i.e. left to right. */
  readonly joins: readonly JoinBar[]
}

export interface BracketLayout {
  /** Total bracket columns: one for the split cell plus one per hierarchy level. */
  readonly columns: number
  readonly rows: readonly BracketRow[]
}

/** Computes the bracket geometry for a design. Pure — no DOM, no styling. */
export function bracketLayout(tree: SubnetTree): BracketLayout {
  const rootPrefix = tree.root.prefix
  const depths = tree.leaves.map((leaf) => leaf.prefix - rootPrefix)
  const maxDepth = depths.length === 0 ? 0 : Math.max(...depths)
  const columns = maxDepth + 1

  const rows = tree.leaves.map((leaf, index): BracketRow => {
    const depth = depths[index] ?? 0
    const leafNetwork = networkAddress(leaf)
    const joins: JoinBar[] = []

    // Walk ancestors from the deepest upward, so bars read left to right.
    for (let prefix = leaf.prefix - 1; prefix >= rootPrefix; prefix -= 1) {
      const block: Cidr = { address: leafNetwork, prefix }
      // Only the block's first leaf draws its bar; later rows sit under the rowspan.
      if (networkAddress(block) !== leafNetwork) continue
      joins.push({
        cidr: block,
        depth: prefix - rootPrefix,
        rowSpan: countLeavesIn(tree, block),
      })
    }

    // Every ancestor covers this row and owns one column, whether its bar starts
    // here or further up, so the split cell takes whatever is left.
    return { leaf, depth, splitColSpan: columns - depth, joins }
  })

  return { columns, rows }
}

/** How many leaves fall inside `block`. */
export function countLeavesIn(tree: SubnetTree, block: Cidr): number {
  return tree.leaves.filter((leaf) => contains(block, leaf)).length
}

/** Fraction of the root's address space this block covers, in (0, 1]. */
export function shareOfRoot(tree: SubnetTree, block: Cidr): number {
  return addressCount(block.prefix) / addressCount(tree.root.prefix)
}
